/**
 * Sibling Variant Radios Web Component
 * Handles variant selection for products with sibling products via metaobjects
 */

class SiblingVariantRadios extends HTMLElement {
  constructor() {
    super()
    this.siblingProducts = []
    this.handleChange = this.handleChange.bind(this)
    this.triggerNotifyMe = this.triggerNotifyMe.bind(this)
  }

  // Initialize the component by loading data and setting up event listeners
  init() {
    this.loadSiblingProducts()
  }

  connectedCallback() {
    this.isProductCard = this.dataset.placement === 'product-card'
    this.init()
    if (!this.isProductCard) {
      this.syncCheckedColorDisabled()
    }
    this.addEventListener('change', this.handleChange)
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange)
  }

  // Load sibling products data from the component's data attribute
  loadSiblingProducts() {
    try {
      this.siblingProducts = this.dataset.siblingProducts
        ? JSON.parse(this.dataset.siblingProducts)
        : []
    } catch (error) {
      this.siblingProducts = []
    }
  }

  // Handle radio button changes - color changes load sibling products, others update current product
  async handleChange(event) {
    if (event.target.type !== 'radio') return

    if (this.isColorOption(event.target)) {
      await this.handleColorChange(event.target.value)
    } else {
      // Handle size/other options with standard variant logic
      this.handleStandardVariantChange()
      this.syncCheckedColorDisabled()
    }
  }

  // Check if the input field is for color/colour options
  isColorOption(input) {
    const fieldset = input.closest('fieldset')
    const legend = fieldset?.querySelector('legend')
    const text = legend?.textContent.toLowerCase() || ''
    return text.includes('color') || text.includes('colour')
  }

  // Handle color changes by loading the appropriate sibling product
  async handleColorChange(selectedColor) {
    const siblingProduct = this.findSiblingByColor(selectedColor)

    if (siblingProduct) {
      if (this.isProductCard) {
        this.dispatchEvent(
          new CustomEvent('product-card:reload', {
            detail: siblingProduct,
          })
        )
      } else {
        await this.loadSiblingProduct(siblingProduct)
      }
    }
  }

  // Find a sibling product that has the selected color as one of its variant options
  findSiblingByColor(color) {
    return this.siblingProducts.find((product) =>
      product.variants?.some((variant) =>
        [variant.option1, variant.option2, variant.option3].includes(color)
      )
    )
  }

  // Load sibling product section and replace entire section content
  async loadSiblingProduct(siblingProduct) {
    const handle = this.getProductHandle(siblingProduct)

    try {
      // Get current size selection and find matching variant in sibling product
      const currentSizeValue = this.getCurrentSizeValue()
      const targetVariantId = this.findMatchingVariantId(
        siblingProduct,
        currentSizeValue
      )
      // Build URL with variant parameter if we have a matching variant
      let url = `/products/${handle}?section_id=${this.dataset.section}`
      if (targetVariantId) {
        url += `&variant=${targetVariantId}`
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        mode: 'no-cors',
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch sibling product: ${response.status} ${response.statusText}`
        )
      }

      const html = await response.text()
      this.replaceSection(html, siblingProduct)

      // Update URL with the variant parameter
      if (this.dataset.updateUrl !== 'false') {
        let newUrl = `/products/${handle}`
        if (targetVariantId) {
          newUrl += `?variant=${targetVariantId}`
        }
        window.history.replaceState({}, '', newUrl)
      }
    } catch (error) {
      console.warn('Failed to load sibling product:', {
        handle,
        section: this.dataset.section,
        error: error.message,
      })
      // Keep current product - user experience remains intact
    }
  }

  // Find the variant ID in the sibling product that matches the current size selection
  findMatchingVariantId(siblingProduct, currentSizeValue) {
    if (!currentSizeValue || !siblingProduct.variants) return null

    // Find the variant that has the same size as the current selection
    const matchingVariant = siblingProduct.variants.find((variant) => {
      // Check if any of the variant options match the current size value
      return [variant.option1, variant.option2, variant.option3].includes(
        currentSizeValue
      )
    })

    return matchingVariant ? matchingVariant.id : null
  }

  // Extract the product handle from sibling product data
  getProductHandle(product) {
    if (product.handle) return product.handle
    if (product.url) {
      const match = product.url.match(/\/products\/([^/?]+)/)
      return match?.[1]
    }
    return null
  }

  // Replace the entire section with new HTML and reinitialize all components
  replaceSection(html, siblingProduct) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const newSection = doc.querySelector(
      `[data-section="${this.dataset.section}"]`
    )
    const currentSection = document.querySelector(
      `[data-section="${this.dataset.section}"]`
    )

    if (newSection && currentSection) {
      // Point Bazaarvoice containers at the sibling product BEFORE they enter the
      // DOM, so bv.js's MutationObserver renders the new product's reviews on its
      // first scan instead of throwing "requires a product Id".
      this.updateBazaarvoiceProductId(newSection, siblingProduct)

      // Replace the entire section
      currentSection.parentNode.replaceChild(newSection, currentSection)

      // Reinitialize the data-option-value event listeners
      this.reinitializeOptionValueListeners()
      document.dispatchEvent(new CustomEvent('product:reload'))
    }
  }

  // Stamp the sibling product's id onto Bazaarvoice review/rating containers.
  // The reviews app block renders without an id in section-rendering fragments,
  // so we set it from the sibling data (the same Shopify product id BV expects,
  // matching card-product.liquid's data-bv-product-id).
  updateBazaarvoiceProductId(root, siblingProduct) {
    const productId = siblingProduct?.id
    if (!productId) return

    root
      .querySelectorAll('[data-bv-show]')
      .forEach((el) => el.setAttribute('data-bv-product-id', productId))
  }

  // Reinitialize event listeners for data-option-value spans
  reinitializeOptionValueListeners() {
    const fieldsets = document.querySelectorAll('.js.product-form__input')

    fieldsets.forEach((fieldset) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]')

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            const legendSpan = fieldset.querySelector(
              'legend [data-option-value]'
            )
            legendSpan.textContent = input.nextElementSibling.textContent.trim()
          }
        })

        if (input.checked) {
          input.dispatchEvent(new Event('change'))
        }
      })
    })
  }

  // Handle standard variant changes (size, etc.) within the current product
  handleStandardVariantChange() {
    const variant = this.getCurrentVariant()

    this.updateAvailability(variant)

    if (!variant) {
      this.updateInventoryStatus(true)
      return this.toggleAddButton(true, 'Sold Out', false, false)
    }

    // Update variant input forms
    document
      .querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      )
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]')
        if (input) {
          input.value = variant.id
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })

    // Update URL with variant
    if (this.dataset.updateUrl !== 'false') {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?variant=${variant.id}`
      )
    }

    // Trigger renderProductInfo like standard VariantRadios does
    this.renderProductInfo(variant)
  }

  // Render product info (price, SKU, inventory, etc.) for the selected variant
  renderProductInfo(variant) {
    const requestedVariantId = variant.id

    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`
    )
    const preOrderAvailable = productForm?.dataset.preOrder === 'true'
    // Placeholder inventory until section fetch returns detailed server markup.
    this.updateInventoryStatus(!variant.available)
    this.toggleAddButton(
      !variant.available,
      window.variantStrings?.soldOut || 'Sold Out',
      false,
      preOrderAvailable
    )

    fetch(
      `${window.location.pathname}?variant=${requestedVariantId}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // Prevent unnecessary ui changes from abandoned selections
        if (this.getCurrentVariant()?.id !== requestedVariantId) return

        const html = new DOMParser().parseFromString(responseText, 'text/html')

        // Update price
        const priceSource = html.getElementById(`price-${this.dataset.section}`)
        const priceDest = document.getElementById(
          `price-${this.dataset.section}`
        )
        if (priceSource && priceDest) {
          priceDest.innerHTML = priceSource.innerHTML
          priceDest.classList.remove('visibility-hidden')
        }

        // Update SKU
        const skuSource = html.getElementById(`Sku-${this.dataset.section}`)
        const skuDest = document.getElementById(`Sku-${this.dataset.section}`)
        if (skuSource && skuDest) {
          skuDest.innerHTML = skuSource.innerHTML
          skuDest.classList.toggle(
            'visibility-hidden',
            skuSource.classList.contains('visibility-hidden')
          )
        }

        // Update inventory
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.section}`
        )
        const inventoryDest = document.getElementById(
          `Inventory-${this.dataset.section}`
        )
        if (inventorySource && inventoryDest) {
          inventoryDest.innerHTML = inventorySource.innerHTML
          inventoryDest.classList.toggle(
            'visibility-hidden',
            inventorySource.innerText === ''
          )
        }

        // Update add to cart button
        const formSource = html.getElementById(
          `product-form-${this.dataset.section}`
        )
        if (formSource) {
          const preOrderAvailable = formSource.dataset.preOrder === 'true'
          this.toggleAddButton(
            !variant.available,
            window.variantStrings?.soldOut || 'Sold Out',
            false,
            preOrderAvailable
          )
        }
      })
  }

  // Update add to cart button state
  toggleAddButton(
    isSoldOut = true,
    text,
    modifyClass = true,
    preOrder = false
  ) {
    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`
    )
    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    const addButtonText = productForm.querySelector('[name="add"] > span')
    if (!addButton) return

    const sectionRoot =
      productForm.closest('.shopify-section') ||
      this.closest('.shopify-section')
    const notifyMeButtons = sectionRoot
      ? Array.from(sectionRoot.querySelectorAll('.btn-notifyme'))
      : Array.from(productForm.querySelectorAll('.btn-notifyme'))

    const notifyMeVariants = this.dataset.notifyMeVariants
      ? JSON.parse(this.dataset.notifyMeVariants)
      : []

    if (isSoldOut && !preOrder) {
      addButton.setAttribute('disabled', 'disabled')
      if (text && text == window.variantStrings.soldOut) {
        const currentVariant = this.getCurrentVariant()

        if (
          this.dataset.notifyMe === 'true' ||
          (currentVariant &&
            currentVariant?.sku &&
            notifyMeVariants.includes(currentVariant.sku.toString()))
        ) {
          addButton.classList.add('hidden')
          notifyMeButtons.forEach((button) => {
            button.classList.remove('hidden')
            button.classList.add('inline-block')
            button.removeEventListener('click', this.triggerNotifyMe)
            button.addEventListener('click', this.triggerNotifyMe)
          })
          if (typeof window.ensureKlaviyoBisWhenNotifyVisible === 'function') {
            window.ensureKlaviyoBisWhenNotifyVisible(this.dataset.section)
          }
        } else {
          addButtonText.textContent = text
          addButton.setAttribute('disabled', 'disabled')
          addButton.classList.remove('hidden')

          notifyMeButtons.forEach((button) => {
            button.classList.add('hidden')
            button.classList.remove('inline-block')
            button.removeEventListener('click', this.triggerNotifyMe)
          })
        }
      } else if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      notifyMeButtons.forEach((button) => {
        button.classList.add('hidden')
        button.classList.remove('inline-block')
      })
      addButton.classList.remove('hidden')
      addButtonText.textContent =
        window.variantStrings?.addToCart || 'Add to Cart'
    }
  }

  // Get the currently selected variant based on all selected options
  getCurrentVariant() {
    const variantData = this.getVariantData()
    const selectedOptions = this.getSelectedOptions()

    return variantData.find((variant) =>
      variant.options.every(
        (option, index) => selectedOptions[index] === option
      )
    )
  }

  triggerNotifyMe() {
    if (!window.triggerKlaviyoBisForSection) {
      console.warn('triggerKlaviyoBisForSection not found')
      return
    }
    window.triggerKlaviyoBisForSection(this.dataset.section)
  }

  updateInventoryStatus(isSoldOut) {
    const inventorySource = document.getElementById(
      `Inventory-${this.dataset.section}`
    )
    const inventoryTemplate = document.getElementById(
      `inventory-template-${this.dataset.section}`
    )

    if (inventoryTemplate && inventoryTemplate.content) {
      inventorySource?.classList.add('hidden')

      if (isSoldOut) {
        const errorSpan =
          inventoryTemplate.content.querySelector('[data-error]')
        if (errorSpan) {
          inventorySource.innerHTML = errorSpan.cloneNode(true).innerHTML
          inventorySource.classList.remove('hidden')
        }
      } else {
        const successSpan =
          inventoryTemplate.content.querySelector('[data-success]')
        if (successSpan) {
          inventorySource.innerHTML = successSpan.cloneNode(true).innerHTML
          inventorySource.classList.remove('hidden')
        }
      }
    }
  }

  // Get variant data from the JSON script tag
  getVariantData() {
    const script = this.querySelector('script[type="application/json"]')
    return script ? JSON.parse(script.textContent) : []
  }

  // Get currently selected values from all radio button fieldsets
  getSelectedOptions() {
    return Array.from(this.querySelectorAll('fieldset'))
      .map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked')
        return checked?.value
      })
      .filter(Boolean)
  }

  // Get the current size value selection
  getCurrentSizeValue() {
    const sizeFieldset = this.querySelector('fieldset.fieldset-size')

    const checkedInput = sizeFieldset.querySelector(
      'input[type="radio"]:checked'
    )
    return checkedInput ? checkedInput.value : null
  }

  syncCheckedColorDisabled() {
    const variant = this.getCurrentVariant()
    const isAvailable = variant && variant.available ? variant.available : false

    const checkedOptions = Array.from(
      this.querySelectorAll('input[type="radio"]:checked')
    )

    checkedOptions.forEach((option) => {
      if (isAvailable) {
        option.removeAttribute('aria-disabled')
      } else {
        option.setAttribute('aria-disabled', 'true')
      }
    })
  }

  updateAvailability(currentVariant) {
    // make an array from in
    /** @type {HTMLInputElement[]} */
    const swatches = Array.from(this.querySelectorAll('input[name="Color"]'))

    const fieldsets = Array.from(this.querySelectorAll('fieldset'))

    swatches.forEach((swatch) => {
      const siblingProduct = this.findSiblingByColor(swatch.value)
      if (!siblingProduct) return
      const colorPosition = siblingProduct.options.findIndex(
        (option) => option === 'Color'
      )
      if (colorPosition < 0) return

      const optionCount =
        siblingProduct.options?.length ??
        siblingProduct.variants?.[0]?.options?.length ??
        0
      if (!optionCount) return

      const matchingVariant = siblingProduct.variants?.find((variant) => {
        /** @type {string[]} */
        let currentOptions
        if (currentVariant) {
          currentOptions = [...currentVariant.options]
          currentOptions[colorPosition] = swatch.value
        } else {
          currentOptions = Array.from({ length: optionCount }, (_, idx) => {
            if (idx === colorPosition) return swatch.value
            return (
              fieldsets[idx]?.querySelector('input[type="radio"]:checked')
                ?.value ?? ''
            )
          })
        }

        return variant.options.join('|') === currentOptions.join('|')
      })

      if (matchingVariant?.available) {
        swatch.removeAttribute('aria-disabled')
        swatch.classList.remove('disabled')
      } else {
        swatch.setAttribute('aria-disabled', 'true')
        swatch.classList.add('disabled')
      }
    })
  }
}

customElements.define('sibling-variant-radios', SiblingVariantRadios)
